import { formatISO } from 'date-fns';
import { type FC } from 'react';
import styled from 'styled-components';

import { useFmsTextsQuery } from '@/api/fms/fmsApi';
import { useUserSettingsQuery } from '@/api/userSettings/userSettingsApi';
import { getAuth } from '@/auth/Auth';
import { appConfig } from '@/constants/config';
import { Language } from '@/i18n/Language';
import { getSessionId } from '@/logging/sessionId';

import { Divider } from './Divider';
import { Column, Row } from './Grid';
import { PageHeading } from './PageHeading';
import { ConnectSurface } from './Pretzel/ConnectSurface';
import { WithGap } from './WithGap';

export const UnexpectedError: FC = () => {
  const userSettingsQuery = useUserSettingsQuery();

  const fmsTextsQuery = useFmsTextsQuery(
    userSettingsQuery.data?.language ?? Language.English,
  );

  const texts = fmsTextsQuery.data;

  // Use hardcoded texts as fallback in case there is issues with FMS.
  const header =
    (texts && texts.error.header.general) || 'Something went wrong';

  const descriptionPart1 =
    (texts && texts.error.description.general.part1) ||
    'Please try again later, or contact customer service on email';

  const descriptionPart2 =
    (texts && texts.error.description.general.part2) ||
    'if the issue persists.';

  const siosEmail = (texts && texts.error.siosEmail) || 'sios@storebrand.no';

  const fieldsTitle =
    (texts && texts.error.field.title) || 'If you contact customer service...';
  const fieldsDescription =
    (texts && texts.error.field.description) ||
    'Please take a screenshot of or copy the text below and send it to customer service. This will help us identify the problem and help you even faster!';
  const errorCode = (texts && texts.error.errorCode.title) || 'Error code:';
  const userCmId = (texts && texts.error.field.userCmId) || 'User:';
  const organizationCmId =
    (texts && texts.error.field.organizationCmId) || 'Organization:';
  const url = (texts && texts.error.field.url) || 'Url:';
  const timestamp = (texts && texts.error.field.timestamp) || 'Timestamp:';

  let token;
  let sessionId = '';
  try {
    token = getAuth().getStaleTokenParsed();
    sessionId = getSessionId();
  } catch {
    // Swallow the error since we don't want to throw
    // errors on the error page.
  }

  return (
    <div data-test="error-page">
      <Center>
        <Page>
          <PageHeading header={header} />
          <Row>
            <Column $span={7}>
              <p className="font-body-large">
                {descriptionPart1}{' '}
                <a href={`mailto:${siosEmail}`}>{siosEmail}</a>{' '}
                {descriptionPart2}
              </p>
            </Column>
          </Row>

          <Divider margin="8" />

          <StyledRow>
            <Column $span={6}>
              <ConnectSurface>
                <WithGap gap="16">
                  <WithGap gap="8">
                    <h2>{fieldsTitle}</h2>
                    <p className="font-body">{fieldsDescription}</p>
                  </WithGap>

                  <DefinitionListWithGap>
                    <div>
                      <dt className="font-body-demibold">{errorCode}</dt>
                      <dd className="font-body">traceId:*{sessionId}</dd>
                    </div>

                    <div>
                      <dt className="font-body-demibold">{timestamp}</dt>
                      <dd className="font-body">{formatISO(new Date())}</dd>
                    </div>

                    {token?.cmId && (
                      <div>
                        <dt className="font-body-demibold">{userCmId}</dt>
                        <dd className="font-body">{token.cmId}</dd>
                      </div>
                    )}

                    {token?.signature && (
                      <div>
                        <dt className="font-body-demibold">{userCmId}</dt>
                        <dd className="font-body">{token.signature}</dd>
                      </div>
                    )}

                    {token?.behalfOf?.cmId && (
                      <div>
                        <dt className="font-body-demibold">
                          {organizationCmId}
                        </dt>
                        <dd className="font-body">{token.behalfOf.cmId}</dd>
                      </div>
                    )}

                    <div>
                      <dt className="font-body-demibold">{url}</dt>
                      <DefinitionWithWordBreak className="font-body">
                        {location.href}
                      </DefinitionWithWordBreak>
                    </div>
                  </DefinitionListWithGap>
                </WithGap>
              </ConnectSurface>
            </Column>

            <StyledColumn $span={6}>
              <img
                alt="Something went wrong illustration"
                src="https://assets.storebrand.no/pretzel/illustrations/something-went-wrong.svg"
              />
            </StyledColumn>
          </StyledRow>
        </Page>
      </Center>
    </div>
  );
};

const Center = styled.div`
  max-width: ${appConfig.maxPageWidth};
  margin: 0 auto;
`;

const Page = styled.div`
  margin-top: 72px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 24px;
`;

const StyledRow = styled(Row)`
  margin-top: 8px;
`;

const StyledColumn = styled(Column)`
  display: flex;
  justify-content: center;
`;

const DefinitionListWithGap = styled.dl`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DefinitionWithWordBreak = styled.dd`
  overflow-wrap: break-word;
`;
